import { useState } from "react";
import { api } from "../../../shared/api.js";
import { useFetch } from "../../../shared/useFetch.js";

export type DostatsStatus = {
  enabled: boolean;
  tokenOk?: boolean;
  seasonId?: number;
  error?: string;
};
export type MappingMatchDto = {
  id: number;
  name: string;
  date: string;
  homeName: string;
  awayName: string;
  score?: number;
};
export type MappingLineupRow = {
  id: number;
  name: string;
  jersey: number | null;
  teamId: number;
  teamName: string;
};
export type MappingPlayerRow = {
  id: string;
  teamId: string;
  firstName: string;
  lastName: string;
  number: number | null;
  position: string | null;
  photoUrl: string | null;
  dostatsId: number | null;
  candidates: { remoteId: number; score: number }[];
};
export type MappingView = {
  match: {
    id: string;
    dostatsId: number | null;
    current: MappingMatchDto | null;
    auto: MappingMatchDto | null;
    candidates: MappingMatchDto[];
  };
  lineup: MappingLineupRow[];
  players: MappingPlayerRow[];
};
export type MappedView = {
  dostatsId: number | null;
  players: { id: string; dostatsId: number | null }[];
};
export type AutomapResult = {
  match: MappingMatchDto | null;
  reason?: string;
  saved?: number;
  ambiguous?: unknown[];
  unmapped?: string[];
  candidates?: MappingMatchDto[];
};
export type MappingSaveBody = {
  dostatsMatchId?: number | null;
  players?: Record<string, number | null>;
};

/** dostats status + match mapping (REST). Mapping writes go to the DB,
 *  then the mapping is re-read — this data is not in the replicant. */
export function usePlayerCompareMapping(matchId: string) {
  const status = useFetch<DostatsStatus>("/api/dostats/status");
  const enabled = status.data?.enabled === true;
  const base = `/api/dostats/matches/${encodeURIComponent(matchId)}`;
  // A lightweight (DB only) request for the picker dots — arrives immediately.
  const mapped = useFetch<MappedView>(enabled ? `${base}/mapped` : null);
  // The full view waits for the slow upstream lineup (~2s) — it is loaded only
  // when the "Xaritalash" section is opened.
  const [wantFull, setWantFull] = useState(false);
  const mapping = useFetch<MappingView>(enabled && wantFull ? `${base}/mapping` : null);
  const [busy, setBusy] = useState(false);
  const refetchAll = () => {
    mapped.refetch();
    mapping.refetch();
  };

  const automap = async (): Promise<AutomapResult> => {
    setBusy(true);
    try {
      const r = await api.post<AutomapResult>(
        `/api/dostats/matches/${encodeURIComponent(matchId)}/automap`,
      );
      refetchAll();
      return r;
    } finally {
      setBusy(false);
    }
  };

  const save = async (body: MappingSaveBody): Promise<void> => {
    setBusy(true);
    try {
      await api.put(`/api/dostats/matches/${encodeURIComponent(matchId)}/mapping`, body);
      refetchAll();
    } finally {
      setBusy(false);
    }
  };

  /** Player id → dostats id (for the picker dot). */
  const mappedIds = new Map((mapped.data?.players ?? []).map((p) => [p.id, p.dostatsId]));

  // Picker dot: "loading" if the status or the mapping hasn't arrived yet
  // (once the data arrives — ready, even while a re-request is in flight).
  const mappedLoading = status.loading || (enabled && mapped.loading && !mapped.data);
  const statusError = status.error;
  const loadFull = () => setWantFull(true);
  /** The full view has been requested but hasn't arrived yet. */
  const fullPending = enabled && (!wantFull || (mapping.loading && !mapping.data));

  return {
    status,
    enabled,
    mapping,
    busy,
    automap,
    save,
    mappedIds,
    mappedLoading,
    statusError,
    loadFull,
    fullPending,
  };
}
